import SentryService from '@services/SentryService';

import type { StorageBackendType } from './types';

/**
 * Detect iOS Safari (including iPadOS which reports as "MacIntel").
 * OPFS has known reliability issues on iOS Safari: worker suspension
 * when backgrounded, incomplete flush(), and repeated-operation failures.
 */
export function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPhone, iPad, iPod directly in UA
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as "MacIntel" but has touch support
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Probe browser capabilities to determine the best storage backend.
 *
 * Returns `'opfs'` if the Origin Private File System is available,
 * otherwise falls back to `'indexeddb'`.
 *
 * On iOS Safari, always returns `'indexeddb'` due to known OPFS
 * reliability issues (worker suspension, data loss on backgrounding).
 *
 * Handles missing API, SecurityError in private browsing, and
 * any other unexpected errors gracefully.
 */
export async function selectStorageBackend(): Promise<StorageBackendType> {
  // iOS Safari has known OPFS reliability issues — prefer IndexedDB
  if (isIOSSafari()) {
    SentryService.addBreadcrumb({
      category: 'storage.select',
      message: 'iOS Safari detected, using IndexedDB (OPFS unreliable on iOS)',
      level: 'info',
    });
    return 'indexeddb';
  }

  try {
    await navigator.storage.getDirectory();

    SentryService.addBreadcrumb({
      category: 'storage.select',
      message: 'OPFS available',
      level: 'info',
    });

    return 'opfs';
  } catch {
    SentryService.addBreadcrumb({
      category: 'storage.select',
      message: 'OPFS not available, falling back to IndexedDB',
      level: 'info',
    });

    return 'indexeddb';
  }
}
